/**
 * KONSERTENS ØKONOMI HOS ARRANGØREN (SPILLESTED-KONSERT §2, skjerm 10; agent E).
 *
 * Budsjett, avtaler, oppgjør og regionbeskyttelse. ALT HER ER BARE ARRANGØRENS — cellene
 * `concert_budget:read` og `concert_budget:update` — og deles aldri med huset, byrået eller artisten.
 *
 * Linjene som hentes av seg selv (leie, honorar, hotell, mat) lagres ikke: de regnes ved lesing
 * fra kildene sine og vises med «Fra». En linje uten id i `concert_budget_lines` kan derfor ikke
 * rettes eller slettes her — den rettes der den kommer fra.
 */
import { z } from 'zod';

import { CONCERT_DEAL_TYPES } from '../concerts/deal-math.js';

const money = z.number().int().min(-1_000_000_000_000).max(1_000_000_000_000);
const nonNegativeMoney = z.number().int().min(0).max(1_000_000_000_000);
const count = z.number().int().min(0).max(10_000_000);
const percent = z.number().min(0).max(100);
const currency = z.string().regex(/^[A-Z]{3}$/);

export const concertDealTypeSchema = z.enum(CONCERT_DEAL_TYPES);

/**
 * Hvor en linje kommer fra. `manual` og `template` er rader i `concert_budget_lines`; resten
 * regnes ved lesing og kan ikke rettes her.
 */
export const CONCERT_BUDGET_LINE_SOURCES = [
  'manual',
  'template',
  'rent',
  'fee_deal',
  'fee_contract',
  'hotel',
  'food',
] as const;
export const concertBudgetLineSourceSchema = z.enum(CONCERT_BUDGET_LINE_SOURCES);
export type ConcertBudgetLineSource = z.infer<typeof concertBudgetLineSourceSchema>;

/** Merket en manuell linje kan bære, så den erstatter en hentet linje uten pris (hotell). */
export const CONCERT_BUDGET_LINE_CATEGORIES = ['accommodation', 'food'] as const;
export const concertBudgetLineCategorySchema = z.enum(CONCERT_BUDGET_LINE_CATEGORIES);

export const concertBudgetLineSchema = z.object({
  /** Radens id for `manual`/`template`; en stabil nøkkel («rent:<id>») for de hentede. */
  id: z.string(),
  source: concertBudgetLineSourceSchema,
  editable: z.boolean(),
  /** Teksten på en manuell linje. Null på en hentet: klienten setter den sammen av `subject`. */
  description: z.string().nullable(),
  /** «Fra» på en manuell linje (fri tekst, f.eks. «Havnehuset»). */
  from: z.string().nullable(),
  category: concertBudgetLineCategorySchema.nullable(),
  /** Hva en hentet linje gjelder: salen, artisten. */
  subject: z.string().nullable(),
  /** Antall rom på en hotellinje. */
  count: z.number().int().nullable(),
  amountMinor: money,
  actualAmountMinor: money.nullable(),
  currency,
  /** En hentet linje uten beløp («legg inn pris»). Regnes som 0. */
  needsPrice: z.boolean(),
  /** Regnes med i summen. En linje i en annen valuta enn konserten gjør ikke det. */
  counted: z.boolean(),
  /** Programposten en honorarlinje gjelder, så «Kontrakt» kan lenke dit. */
  performanceId: z.uuid().nullable(),
  sortOrder: z.number().int(),
});
export type ConcertBudgetLine = z.infer<typeof concertBudgetLineSchema>;

/** Programpostens kontraktfane, når kontrakten finnes. */
export const concertContractLinkSchema = z.object({
  festivalId: z.uuid(),
  editionId: z.uuid(),
  performanceId: z.uuid(),
});

/**
 * BUDET AVTALEN KOM FRA (QA runde 1 av spillesteder, P1-2, 0430). Det byrået godtok, slik det
 * sto. `dealType` er null når budet bare sa beløpet — da er det bare beløpet som sammenlignes.
 */
export const concertBidTermsSchema = z.object({
  bidId: z.uuid(),
  dealType: concertDealTypeSchema.nullable(),
  guaranteeMinor: nonNegativeMoney,
  artistPercent: percent.nullable(),
  afterCosts: z.boolean().nullable(),
  currency,
  /**
   * QA16 funn 14 (C2): `sent` while the bid is out — the Avtaler tab shows it at once, «Sendt i
   * budet» — and `accepted` once the agency said yes and the deal was filled from it.
   */
  status: z.enum(['sent', 'accepted']).default('accepted'),
});
export type ConcertBidTerms = z.infer<typeof concertBidTermsSchema>;

/** Avtalen med én artist på konserten. Én per programpost, også når ingen avtale er ført. */
export const concertArtistDealSchema = z.object({
  performanceId: z.uuid(),
  artistName: z.string(),
  billing: z.enum(['headliner', 'support']).nullable(),
  /** Den som regnes som hovedartist i budsjettet. */
  isHeadliner: z.boolean(),
  dealId: z.uuid().nullable(),
  dealType: concertDealTypeSchema.nullable(),
  guaranteeMinor: nonNegativeMoney.nullable(),
  artistPercent: percent.nullable(),
  afterCosts: z.boolean(),
  currency,
  notes: z.string().nullable(),
  /** Honoraret i byråkontrakten, når den finnes. Brukes som garanti når ingen avtale er ført. */
  contractFeeMinor: money.nullable(),
  contractFeeCurrency: currency.nullable(),
  contract: concertContractLinkSchema.nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** Det godtatte budet avtalen ble fylt fra, når det finnes. */
  bidTerms: concertBidTermsSchema.nullable(),
  /** Avtalen er endret etter at budet ble godtatt: «Avviker fra budet». */
  deviatesFromBid: z.boolean(),
});
export type ConcertArtistDeal = z.infer<typeof concertArtistDealSchema>;

/** Ett antall billetter, regnet ut. Samme form som `TicketOutcome` i `deal-math.ts`. */
export const concertOutcomeSchema = z.object({
  tickets: count,
  grossMinor: money,
  feesMinor: money,
  netMinor: money,
  costsMinor: money,
  splitBaseMinor: money.nullable(),
  percentShareMinor: money.nullable(),
  artistShareMinor: money,
  guaranteeApplies: z.boolean(),
  resultMinor: money,
});
export type ConcertOutcome = z.infer<typeof concertOutcomeSchema>;

export const concertEconomicsSchema = z.object({
  scenarios: z.object({
    low: concertOutcomeSchema.nullable(),
    estimate: concertOutcomeSchema.nullable(),
    soldOut: concertOutcomeSchema.nullable(),
  }),
  breakEvenTickets: count.nullable(),
  breakEvenCeiling: count,
});

/** Avtalen regnestykket bruker for hovedartisten, og hvor den kom fra. */
export const concertEffectiveDealSchema = z.object({
  performanceId: z.uuid(),
  artistName: z.string(),
  source: z.enum(['deal', 'contract']),
  dealType: concertDealTypeSchema,
  guaranteeMinor: nonNegativeMoney,
  artistPercent: percent,
  afterCosts: z.boolean(),
});

export const concertBudgetInputsSchema = z.object({
  ticketPriceMinor: nonNegativeMoney.nullable(),
  ticketFeePercent: percent.nullable(),
  capacity: count.nullable(),
  /**
   * Salens kapasitet (valgt oppsett, ellers stående) når konserten ikke har sin egen og står i en
   * sal i Tvitvi. Regnestykket bruker den da; web skriver «· fra salen» (QA runde 1).
   */
  roomCapacity: count.nullable().optional(),
  expectedAudience: count.nullable(),
  lowAudience: count.nullable(),
});
export type ConcertBudgetInputs = z.infer<typeof concertBudgetInputsSchema>;

export const CONCERT_SETTLEMENT_STATUSES = ['draft', 'final'] as const;

/** Én linje i oppgjørets øyeblikksbilde. */
export const concertSettlementLineSchema = z.object({
  lineId: z.string(),
  source: concertBudgetLineSourceSchema,
  description: z.string().nullable(),
  subject: z.string().nullable(),
  count: z.number().int().nullable(),
  budgetMinor: money,
  actualMinor: money,
});

export const concertSettlementDtoSchema = z.object({
  id: z.uuid(),
  status: z.enum(CONCERT_SETTLEMENT_STATUSES),
  ticketsSold: count,
  ticketPriceMinor: nonNegativeMoney,
  ticketFeePercent: percent,
  grossMinor: money,
  feesMinor: money,
  netMinor: money,
  costsMinor: money,
  artistShareMinor: money,
  resultMinor: money,
  splitBaseMinor: money.nullable(),
  percentShareMinor: money.nullable(),
  guaranteeApplies: z.boolean(),
  currency,
  lines: z.array(concertSettlementLineSchema),
  deal: concertEffectiveDealSchema.nullable(),
  note: z.string().nullable(),
  finalizedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ConcertSettlementDto = z.infer<typeof concertSettlementDtoSchema>;

export const concertBudgetDtoSchema = z
  .object({
    concertId: z.uuid(),
    /** Huset som eier budsjettet, til banneret «Bare ‹hus›». */
    organizationName: z.string(),
    startsOn: z.iso.date(),
    status: z.string(),
    currency,
    /** «Lag oppgjør» åpner seg dagen etter konserten. */
    settlementOpen: z.boolean(),
    inputs: concertBudgetInputsSchema,
    lines: z.array(concertBudgetLineSchema),
    costsMinor: money,
    artists: z.array(concertArtistDealSchema),
    deal: concertEffectiveDealSchema.nullable(),
    economics: concertEconomicsSchema,
    settlement: concertSettlementDtoSchema.nullable(),
  })
  .meta({ id: 'ConcertBudget' });
export type ConcertBudgetDto = z.infer<typeof concertBudgetDtoSchema>;

export const updateConcertBudgetRequestSchema = z
  .object({
    ticketPriceMinor: nonNegativeMoney.nullable().optional(),
    ticketFeePercent: percent.nullable().optional(),
    capacity: count.nullable().optional(),
    expectedAudience: count.nullable().optional(),
    lowAudience: count.nullable().optional(),
  })
  .meta({ id: 'UpdateConcertBudgetRequest' });
export type UpdateConcertBudgetRequest = z.infer<typeof updateConcertBudgetRequestSchema>;

const descriptionSchema = z.string().trim().min(1).max(200);
const fromSchema = z.string().trim().max(120);

export const createConcertBudgetLineRequestSchema = z
  .object({
    description: descriptionSchema,
    from: fromSchema.nullable().optional(),
    amountMinor: money.default(0),
    category: concertBudgetLineCategorySchema.nullable().optional(),
  })
  .meta({ id: 'CreateConcertBudgetLineRequest' });
export type CreateConcertBudgetLineRequest = z.infer<typeof createConcertBudgetLineRequestSchema>;

export const updateConcertBudgetLineRequestSchema = z
  .object({
    description: descriptionSchema.optional(),
    from: fromSchema.nullable().optional(),
    amountMinor: money.optional(),
    actualAmountMinor: money.nullable().optional(),
  })
  .meta({ id: 'UpdateConcertBudgetLineRequest' });
export type UpdateConcertBudgetLineRequest = z.infer<typeof updateConcertBudgetLineRequestSchema>;

export const upsertConcertDealRequestSchema = z
  .object({
    dealType: concertDealTypeSchema,
    guaranteeMinor: nonNegativeMoney.nullable(),
    artistPercent: percent.nullable(),
    afterCosts: z.boolean().default(true),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .meta({ id: 'UpsertConcertDealRequest' });
export type UpsertConcertDealRequest = z.infer<typeof upsertConcertDealRequestSchema>;

export const updateConcertRegionRequestSchema = z
  .object({
    radiusKm: z.number().int().min(0).max(20_000).nullable(),
    daysBefore: z.number().int().min(0).max(365).nullable(),
    daysAfter: z.number().int().min(0).max(365).nullable(),
  })
  .meta({ id: 'UpdateConcertRegionRequest' });
export type UpdateConcertRegionRequest = z.infer<typeof updateConcertRegionRequestSchema>;

export const saveConcertSettlementRequestSchema = z
  .object({
    ticketsSold: count,
    ticketPriceMinor: nonNegativeMoney,
    ticketFeePercent: percent.nullable().optional(),
    note: z.string().trim().max(4000).nullable().optional(),
    /** Faktiske beløp på de manuelle linjene. Uten et beløp gjelder budsjettet. */
    actuals: z
      .array(z.object({ lineId: z.uuid(), actualAmountMinor: money.nullable() }))
      .max(500)
      .default([]),
  })
  .meta({ id: 'SaveConcertSettlementRequest' });
export type SaveConcertSettlementRequest = z.infer<typeof saveConcertSettlementRequestSchema>;

/** Leieavtalen med huset, lest fra arrangørens kopi av den bekreftede forespørselen. */
export const concertRentAgreementSchema = z.object({
  dateRequestId: z.uuid(),
  venueName: z.string().nullable(),
  roomName: z.string().nullable(),
  requestedOn: z.iso.date(),
  status: z.string(),
  rentMinor: nonNegativeMoney.nullable(),
  rentCurrency: currency,
  rentTerms: z.string().nullable(),
  /** Husets andel av merch fra husets svar (0433, sak 222.24). */
  merchPercent: z.number().nullable(),
  confirmedAt: z.iso.datetime().nullable(),
});

export const concertRegionTermsSchema = z.object({
  radiusKm: z.number().int().nullable(),
  daysBefore: z.number().int().nullable(),
  daysAfter: z.number().int().nullable(),
});

/**
 * En konsert som bryter et vilkår. `direction` sier hvem sitt: `ours` er denne konsertens vilkår
 * brutt av den andre, `theirs` er den andre konsertens vilkår brutt av denne.
 */
export const concertRegionWarningSchema = z.object({
  otherConcertId: z.uuid(),
  direction: z.enum(['ours', 'theirs']),
  artistName: z.string(),
  otherStartsOn: z.iso.date(),
  otherCity: z.string().nullable(),
  otherVenueName: z.string().nullable(),
  /** Avstanden når begge har koordinater; ellers null (sammenlignet på by). */
  distanceKm: z.number().nullable(),
});
export type ConcertRegionWarning = z.infer<typeof concertRegionWarningSchema>;

export const concertRegionWarningsDtoSchema = z
  .object({ items: z.array(concertRegionWarningSchema) })
  .meta({ id: 'ConcertRegionWarnings' });
export type ConcertRegionWarningsDto = z.infer<typeof concertRegionWarningsDtoSchema>;

export const concertAgreementsDtoSchema = z
  .object({
    concertId: z.uuid(),
    currency,
    artists: z.array(concertArtistDealSchema),
    rent: concertRentAgreementSchema.nullable(),
    region: concertRegionTermsSchema,
    regionWarnings: z.array(concertRegionWarningSchema),
  })
  .meta({ id: 'ConcertAgreements' });
export type ConcertAgreementsDto = z.infer<typeof concertAgreementsDtoSchema>;

/** Det konsertlista viser per konsert (agent D), bare for den som har `concert_budget:read`. */
export interface ConcertEconomySummary {
  breakEvenTickets: number | null;
  dealType: string | null;
  /** «70/30» for en prosentavtale; typen oversettes av klienten. */
  dealLabel: string | null;
  /** QA16 funn 12: the deal's terms, so the list reads «Versus: 30 000 kr i garanti eller 70 % …». */
  dealGuaranteeMinor: number | null;
  dealArtistPercent: number | null;
  dealAfterCosts: boolean | null;
  dealCurrency: string | null;
  estimatedResultMinor: number | null;
}
